/*
 * Records whose pins would sit exactly on top of each other (two jobs at one
 * address) share one pin with a count, and its popup lists them: otherwise
 * only the top one could ever be tapped. "Exactly" is the coordinates rounded
 * to 5 decimal places, about a metre, the same rule as Android's map; pins a
 * few metres apart stay separate, as before. The groups come from the rows
 * shown, so when one record leaves (finished, filtered out) the other's pin
 * goes back to a plain pin by itself.
 */

export type PinPoint = {lat: number; lng: number};

export interface PinGroup<T> {
  /** A lone record keeps a key of its own; a shared spot is keyed by the spot. */
  key: string;
  position: PinPoint;
  rows: T[];
}

/** The ~1 m cell a point falls in. */
export const spotOf = (at: PinPoint): string => `${at.lat.toFixed(5)},${at.lng.toFixed(5)}`;

/**
 * The rows with a position, one group per spot, in the order each spot first
 * appears; inside a group, the rows keep their order. A lone row's key is
 * `row:<its key>` and a shared spot's is `spot:<lat>,<lng>`, so a pin stays
 * the same marker while it holds the same record(s).
 */
export function groupPins<T>(
  rows: T[],
  positionOf: (row: T) => PinPoint | null,
  keyOf: (row: T) => string | number,
): PinGroup<T>[] {
  const bySpot = new Map<string, {position: PinPoint; rows: T[]}>();
  rows.forEach(row => {
    const position = positionOf(row);
    if (!position) return;
    const spot = spotOf(position);
    const group = bySpot.get(spot);
    if (group) group.rows.push(row);
    else bySpot.set(spot, {position, rows: [row]});
  });
  return Array.from(bySpot, ([spot, group]) => ({
    key: group.rows.length === 1 ? `row:${keyOf(group.rows[0])}` : `spot:${spot}`,
    position: group.position,
    rows: group.rows,
  }));
}
